import React, { useEffect, useMemo, useState } from "react";
import { getBookings } from "@/api";
import { STATUSES } from "./adminShared";
import BookingDetail from "./BookingDetail";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  useEffect(() => {
    getBookings().then(setBookings).catch(() => {});
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      (map[b.date] = map[b.date] || []).push(b);
    });
    return map;
  }, [bookings]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      arr.push(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    return arr;
  }, [cursor]);

  const move = (delta) =>
    setCursor(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  const today = new Date().toISOString().slice(0, 10);

  const onUpdated = (updated, deletedId) => {
    if (deletedId) setBookings((bs) => bs.filter((x) => x.id !== deletedId));
    else {
      setBookings((bs) => bs.map((x) => (x.id === updated.id ? updated : x)));
      setSelected(updated);
    }
  };

  return (
    <div data-testid="admin-calendar">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Operaciones</p>
          <h1 className="font-serif-hc font-light text-3xl md:text-4xl text-[#F5F2EA]">Calendario</h1>
        </div>
        <div className="flex items-center gap-4">
          <button data-testid="calendar-prev" onClick={() => move(-1)} className="p-2 border border-[#1E2B25] text-[#A3A095] hover:text-[#C4A661] hover:border-[#C4A661]/50 transition-colors" aria-label="Mes anterior">
            <ChevronLeft size={16} />
          </button>
          <p data-testid="calendar-month" className="font-serif-hc text-xl text-[#F5F2EA] min-w-[170px] text-center">
            {MONTHS[cursor.m]} {cursor.y}
          </p>
          <button data-testid="calendar-next" onClick={() => move(1)} className="p-2 border border-[#1E2B25] text-[#A3A095] hover:text-[#C4A661] hover:border-[#C4A661]/50 transition-colors" aria-label="Mes siguiente">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-7 border-l border-t border-[#1E2B25]">
        {WEEKDAYS.map((d) => (
          <div key={d} className="p-2 md:p-3 text-[10px] tracking-[0.2em] uppercase text-[#A3A095] border-r border-b border-[#1E2B25] bg-[#0C1310]">
            {d}
          </div>
        ))}
        {cells.map((date, i) => (
          <div
            key={i}
            data-testid={date ? `calendar-day-${date}` : undefined}
            className={`min-h-[86px] md:min-h-[110px] p-1.5 md:p-2 border-r border-b border-[#1E2B25] ${date === today ? "bg-[#C4A661]/5" : ""}`}
          >
            {date && (
              <>
                <p className={`text-xs ${date === today ? "text-[#C4A661]" : "text-[#A3A095]"}`}>{Number(date.slice(-2))}</p>
                <div className="mt-1 space-y-1">
                  {(byDate[date] || []).slice(0, 3).map((b) => {
                    const s = STATUSES[b.status] || STATUSES.pending_payment;
                    return (
                      <button
                        key={b.id}
                        data-testid={`calendar-booking-${b.id}`}
                        onClick={() => setSelected(b)}
                        className="w-full text-left px-1.5 py-1 text-[10px] md:text-[11px] leading-tight truncate border-l-2 bg-[#0C1310] hover:bg-[#152420] transition-colors"
                        style={{ borderColor: s.color, color: "#F5F2EA" }}
                        title={`${b.name} · ${b.tour_name}`}
                      >
                        {b.name}
                      </button>
                    );
                  })}
                  {(byDate[date] || []).length > 3 && (
                    <p className="text-[10px] text-[#A3A095] px-1">+{byDate[date].length - 3} más</p>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Leyenda */}
      <div className="mt-5 flex flex-wrap gap-4">
        {Object.entries(STATUSES).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase text-[#A3A095]">
            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} /> {s.label}
          </span>
        ))}
      </div>

      <BookingDetail booking={selected} open={!!selected} onClose={() => setSelected(null)} onUpdated={onUpdated} />
    </div>
  );
}
